/* SOLE 2026 — gift with purchase (config + rationale in snippets/sole-free-gift.liquid).

   Keeps the free gift line in sync with its trigger products (plural since 2026-09-02: F83 + E35):
     any trigger in cart, gift missing  -> add the gift
     gift in cart, every trigger gone   -> remove the orphan

   ONE gift per cart even when two qualifying machines are in it. That is not a policy this file invents:
   the Admin discounts cap the free line at quantity 1, verified by cart probe 2026-09-02.

   The DISCOUNT is what makes the gift free, server-side, on every recalculation including checkout.
   This file only saves the shopper a step. It cannot cause an incorrect charge. */
import { ThemeEvents, CartAddEvent, CartUpdateEvent } from '@theme/events';

const cfgEl = document.getElementById('sole-free-gift-config');
if (cfgEl) {
  let cfg;
  try {
    cfg = JSON.parse(cfgEl.textContent || '{}');
  } catch (e) {
    cfg = null;
  }

  if (cfg && cfg.gift && Array.isArray(cfg.trigger) && cfg.trigger.length) {
    const triggers = cfg.trigger.map(Number);
    const gift = Number(cfg.gift);
    // Session-scoped, so a shopper who deletes the gift is not fought with. Re-adding on every render is
    // a dark pattern and it races the removal UI. The recovery path is the "See the mat" link in the PDP
    // gift row, which reaches the mat's own product page where the automatic discount still prices it at
    // $0. That link is load-bearing, not garnish: it is also the only route back for a shopper whose add
    // failed at the !res.ok return below, which is deliberately silent.
    const GIFT_PROP = '_free_gift';
    const DECLINED = 'sole-free-gift-declined-' + gift;
    // cart:update is dispatched by both adds and updates, and our own writes dispatch it again, so
    // without this the handler re-enters itself.
    let busy = false;
    // Whether the gift was in the cart last time we looked. This is what tells a shopper's deliberate
    // removal apart from "it was never added": trigger still present, gift present a moment ago and
    // gone now, means they took it out on purpose. Without this the gift is un-removable, which is
    // both a dark pattern and a loop against the removal UI.
    let sawGift = false;

    const cartUrl = () => (window.Theme?.routes?.cart_url || '/cart').replace(/(\.js)?$/, '.js');
    const addUrl = () => window.Theme?.routes?.cart_add_url || '/cart/add.js';
    const changeUrl = () => window.Theme?.routes?.cart_change_url || '/cart/change.js';

    const sectionsToRender = () => {
      const ids = new Set(['cart-drawer']);
      document.querySelectorAll('cart-items-component[data-section-id]').forEach((c) => {
        if (c instanceof HTMLElement && c.dataset.sectionId) ids.add(c.dataset.sectionId);
      });
      return Array.from(ids).join(',');
    };

    // Add uses CartAddEvent; removal uses CartUpdateEvent. Both carry the same event name, but the
    // drawer's auto-open handler fires on either, so the distinction is about intent rather than
    // mechanism: a removal must not read as "something was added to your cart".
    const announce = (Ctor, json, count) => {
      document.dispatchEvent(
        new Ctor({ item_count: count }, 'sole-free-gift', {
          source: 'sole-free-gift',
          itemCount: 1,
          sections: json && json.sections,
        })
      );
    };

    const sync = async () => {
      if (busy) return;
      busy = true;
      try {
        const cart = await (await fetch(cartUrl(), { headers: { Accept: 'application/json' } })).json();
        const items = cart.items || [];
        // The MATCHED trigger line, not just a boolean. The `_free_gift` property below lands on the real
        // order, and a static "Free with your SOLE F83" became wrong the moment the offer covered a second
        // machine: an E35 order would have shipped a mat labelled as an F83 gift. Naming the line that
        // actually earned it keeps ops able to read the order.
        const trigLine = items.find((i) => triggers.indexOf(Number(i.variant_id)) !== -1);
        const hasTrigger = !!trigLine;
        // Match on the PROPERTY we stamped, not on the variant alone. Identifying the gift by
        // variant id meant any mat in the cart looked like our gift, so the orphan-removal below
        // deleted a mat the shopper had deliberately added - the product could not be bought on its
        // own at all. Reported live 2026-08-31. The `_free_gift` stamp set on the add path is what
        // distinguishes our line from theirs, and it was already there; it just was not read.
        const isOurs = (i) => !!(i.properties && (i.properties._free_gift || i.properties[GIFT_PROP]));
        const giftLine = items.find((i) => Number(i.variant_id) === gift && isOurs(i));
        // A mat the shopper added themselves. Never touched, and it does not suppress the offer.
        const ownedMat = items.find((i) => Number(i.variant_id) === gift && !isOurs(i));

        // `!ownedMat` matters: if they already added a mat themselves, the Buy-X-Get-Y discounts THAT
        // line the moment the F83 lands, so they already have the free mat. Adding a second would
        // hand them one free and one charged - a mat they never asked for, on an order they were
        // about to place.
        if (hasTrigger && !giftLine && !ownedMat) {
          // They had it and now they do not, with the machine still in the cart. That is a removal.
          if (sawGift) {
            try {
              sessionStorage.setItem(DECLINED, '1');
            } catch (e) {
              /* ignore */
            }
            sawGift = false;
            return;
          }
          let declined = false;
          try {
            declined = sessionStorage.getItem(DECLINED) === '1';
          } catch (e) {
            /* private mode: fall through and add, the shopper can remove it */
          }
          if (declined) return;

          const res = await fetch(addUrl(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: [
                {
                  id: gift,
                  quantity: 1,
                  properties: {
                    _free_gift: trigLine ? 'Free with your ' + trigLine.product_title : cfg.property || 'gift',
                  },
                },
              ],
              sections: sectionsToRender(),
              sections_url: window.location.pathname,
            }),
          });
          if (!res.ok) return; // the offer still stands, the shopper just adds it themselves
          const json = await res.json();
          sawGift = true;
          announce(CartAddEvent, json, (cart.item_count || 0) + 1);
          return;
        }

        sawGift = !!giftLine;

        if (giftLine && !hasTrigger) {
          // The discount stops applying the moment the trigger leaves, so an orphan would silently
          // reprice to full. Remove it by line KEY, never by position: /cart/change.js accepts the key
          // and that sidesteps the index race the quantity component warns about.
          // Deliberately NOT marking this declined. They removed the MACHINE, not the gift. If they add
          // the F83 back the gift should come back with it; sawGift going false below is enough to let
          // that happen without re-adding an orphan in the meantime.
          const res = await fetch(changeUrl(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              id: giftLine.key,
              quantity: 0,
              sections: sectionsToRender(),
              sections_url: window.location.pathname,
            }),
          });
          if (!res.ok) return;
          const json = await res.json();
          sawGift = false;
          announce(CartUpdateEvent, json, Math.max((cart.item_count || 1) - giftLine.quantity, 0));
        }
      } catch (e) {
        /* Never surface this. The gift is a bonus; the purchase is not. */
      } finally {
        busy = false;
      }
    };

    // A shopper removing the gift by hand must stick: cart:update fires after their removal, at which
    // point the trigger is still present and we would otherwise put it straight back. The sawGift
    // check above is what catches that. Our own writes are filtered out by source.
    document.addEventListener(ThemeEvents.cartUpdate, (event) => {
      const src = event && event.detail && event.detail.data && event.detail.data.source;
      if (src === 'sole-free-gift') return;
      sync();
    });

    // Covers arriving on /cart with a trigger already in the cart from a previous session.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', sync, { once: true });
    } else {
      sync();
    }
  }
}
